import { useState, useRef } from 'react';
import NormalTooltipIcon from '@assets/svgs/common/tooltip.svg';
import DisableTooltipIcon from '@assets/svgs/common/disable-tooltip-icon.svg';
import tw from 'twin.macro';
import ToolTip from '../toolTip';

/**
 * TooltipIcon component – icon that shows a tooltip on hover (or touch); optional active/disabled icon states.
 *
 * @param {Object} props
 * @param {string} [props.src] - Image source for the icon in active/default state. Defaults to NormalTooltipIcon.
 * @param {string} [props.text] - Tooltip content shown on hover.
 * @param {boolean} [props.isActiveOnHover] - When true, shows a disabled-style icon until hover, then the active icon and tooltip.
 * @param {boolean} [props.showTriangle] - When true, tooltip shows a triangle pointer (passed to ToolTip).
 * @param {boolean} [props.disableSmartPos] - When true, disables smart positioning (passed to ToolTip).
 * @param {string} [props.srcDiabled] - Image source for the disabled/inactive icon when isActiveOnHover is true. Defaults to DisableTooltipIcon.
 * @param {Object|import('twin.macro').TwStyle} [props.customeStyle] - Fallback styles for the tooltip when toolTipCustomStyle is not set.
 * @param {Object|import('twin.macro').TwStyle} [props.toolTipCustomStyle] - Additional styles for the ToolTip content.
 * @param {Object|import('twin.macro').TwStyle} [props.imageCustomStyle] - Additional styles for the icon image(s).
 * @param {Object|import('twin.macro').TwStyle} [props.rootElementStyle] - Additional styles for the root wrapper.
 * @param {string} [props.placement] - Tooltip placement (e.g. 'top', 'bottom'); passed to ToolTip.
 * @returns {React.ReactElement}
 */
const TooltipIcon = ({
  src = NormalTooltipIcon,
  text,
  isActiveOnHover,
  showTriangle,
  disableSmartPos,
  srcDiabled = DisableTooltipIcon,
  customeStyle,
  toolTipCustomStyle,
  imageCustomStyle,
  rootElementStyle,
  placement,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 200);
  };

  const finalCustomStyle = toolTipCustomStyle || customeStyle;
  // const isMobileScreen;
  return (
    <div
      className="group"
      css={[tw`relative flex items-center justify-center w-[16px] h-[16px]`, rootElementStyle]}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
      // Keyboard users reach it by Tab and close it with Escape, like hovering.
      tabIndex={0}
      aria-label={typeof text === 'string' ? text : undefined}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      onKeyDown={e => e.key === 'Escape' && setIsHovered(false)}
    >
      {isActiveOnHover ? (
        isHovered ? (
          <>
            <img loading="lazy" decoding="async" src={src} alt="tooltip-icon" css={[tw`w-[.80rem]`, imageCustomStyle]} />
            <ToolTip
              text={text}
              toolTipCustomStyle={finalCustomStyle}
              placement={placement}
              isOpen={isHovered}
              showTriangle={showTriangle}
              disableSmartPos={disableSmartPos}
            />
          </>
        ) : (
          // <img src={src} alt="tooltip-icon" tw="w-[.80rem]" />
          <img loading="lazy" decoding="async"
            src={srcDiabled}
            alt="tooltip-icon-disabled"
            css={[tw`w-[.80rem]`, imageCustomStyle]}
          />
        )
      ) : (
        <>
          <img loading="lazy" decoding="async" src={src} alt="tooltip-icon" css={[tw`w-[.80rem]`, imageCustomStyle]} />
          <ToolTip
            text={text}
            toolTipCustomStyle={finalCustomStyle}
            placement={placement}
            isOpen={isHovered}
            showTriangle={showTriangle}
            disableSmartPos={disableSmartPos}
          />
        </>
      )}
    </div>
  );
};

export default TooltipIcon;
